/**
 * Android push registration (SAB-538).
 *
 * iOS registers natively in AppDelegate.swift; web uses the Firebase JS SDK
 * (usePushNotifications). Android registers here via @capacitor-firebase/messaging
 * and POSTs the FCM token to the shared register-device-token function using the
 * env-aware, auth-aware supabase client, so it avoids iOS's hardcoded prod URL and
 * storage-key bug.
 *
 * Only @capacitor-firebase/messaging is used for Android FCM. @capacitor/push-notifications
 * is never register()'d on Android (it is retained only for iOS's APNs bridge and the
 * NotificationBell tray cleanup), so exactly one plugin handles Android messages.
 */
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { supabase } from '@/integrations/supabase/client'
import { handleNotificationTap, defaultTapDeps } from '@/lib/nflPushDeepLink'

let registered = false
let tokenListenerAdded = false
let tapListenerAdded = false

async function postToken(token: string): Promise<void> {
  const { error } = await supabase.functions.invoke('register-device-token', {
    body: { token, platform: 'android' },
  })
  if (error) console.error('[android-push] register-device-token failed', error)
  else console.log('[android-push] token registered')
}

/**
 * Request permission, ensure the display channel exists, mint the FCM token, and
 * register it. No-op off Android. Safe to call repeatedly: it self-guards to run
 * its full flow once per app launch (and resets on failure so a later login can
 * retry). Must be called only when a user session exists so the invoke carries a JWT.
 */
export async function registerAndroidPush(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return
  if (registered) return
  registered = true

  try {
    // Runtime POST_NOTIFICATIONS prompt (Android 13+).
    const perm = await FirebaseMessaging.requestPermissions()
    if (perm.receive !== 'granted') {
      registered = false
      return
    }

    // API 26+ requires a channel before any notification can display. The send
    // path (send-push-notification) targets channel_id 'default', so create it.
    try {
      await FirebaseMessaging.createChannel({
        id: 'default',
        name: 'General',
        importance: 5, // Importance.Max — allows heads-up notifications
      })
    } catch (err) {
      console.warn('[android-push] createChannel failed (non-fatal)', err)
    }

    // Re-POST on token rotation (the onNewToken equivalent).
    if (!tokenListenerAdded) {
      tokenListenerAdded = true
      await FirebaseMessaging.addListener('tokenReceived', (event) => {
        if (event?.token) void postToken(event.token)
      })
    }

    const { token } = await FirebaseMessaging.getToken()
    if (token) await postToken(token)
  } catch (err) {
    registered = false
    console.error('[android-push] registration failed', err)
  }
}

/**
 * Route a tapped notification to the page it is about.
 *
 * ── REGISTERED AT APP START, NOT INSIDE registerAndroidPush ─────────────────
 *
 * registerAndroidPush() is Android-only and runs only once a session exists. Tap routing
 * needs neither: it must work on iOS too, and a cold-start tap arrives before login
 * resolves. So this is separate, and the caller invokes it as early as possible.
 *
 * ── COLD START IS HANDLED BY THE PLUGIN, VERIFIED ──────────────────────────
 *
 * Both platforms emit notificationActionPerformed with retainUntilConsumed = true
 * (ios/Plugin/FirebaseMessagingPlugin.swift:217, android/.../FirebaseMessagingPlugin.java:349),
 * so Capacitor retains a tap fired before any listener exists and replays it to the first
 * one that attaches. Attaching at startup therefore covers the cold-launch case, which is
 * the one that matters most and the one that silently breaks if this assumption changes.
 *
 * Guarded so repeated calls cannot stack duplicate listeners — a doubled listener would
 * navigate twice for one tap.
 */
export async function registerPushTapRouting(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (tapListenerAdded) return
  tapListenerAdded = true

  try {
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      // The FCM `data` block: { type, league_id, ... }, set by _shared/nfl-notifications.ts.
      const data = (event?.notification?.data ?? {}) as Record<string, unknown>
      const result = handleNotificationTap(data, defaultTapDeps())
      if (!result.routed) {
        console.log('[push-tap] no route for', data?.type, '- falling through to landing chain')
      }
    })
  } catch (err) {
    tapListenerAdded = false
    console.error('[push-tap] listener registration failed', err)
  }
}
